import { AudioSample, type AudioTransformOptions } from 'mediabunny';

/** The rates AAC encoders accept; anything else is resampled to 48 kHz. */
export const aacRate = (inputRate: number) => (inputRate === 44100 || inputRate === 48000 ? inputRate : 48000);

/**
 * Runs `transform`, then resamples what it returns to `rateFor(input rate)`. Linear interpolation, carried across
 * blocks so there are no seams: plenty for a scratch copy of a voice that was 16 kHz to begin with. Each output
 * block keeps its input block's timestamp, so placement and gap-filling are unchanged.
 */
export function resampledFor(transform: AudioTransformOptions, rateFor: (inputRate: number) => number): AudioTransformOptions {
  const resample = linearResampler();
  return {
    process: async (sample) => {
      const out = await transform.process!(sample);
      if (!out) return out;
      const one = (s: AudioSample) => (rateFor(s.sampleRate) === s.sampleRate ? s : resample(s, rateFor(s.sampleRate)));
      return Array.isArray(out) ? out.map(one) : one(out);
    },
  };
}

export function linearResampler() {
  let prev: Float32Array | null = null;
  /** Position of the next output frame, in input frames from the start of the next block (-1 = the last frame before it). */
  let pos = 0;
  return (sample: AudioSample, outRate: number): AudioSample => {
    const n = sample.numberOfFrames;
    const channels = sample.numberOfChannels;
    const step = sample.sampleRate / outRate;
    const planes: Float32Array[] = [];
    for (let c = 0; c < channels; c++) {
      const p = new Float32Array(n);
      sample.copyTo(p, { planeIndex: c, format: 'f32-planar' });
      planes.push(p);
    }
    if (!prev || prev.length !== channels) prev = Float32Array.from(planes, (p) => p[0] ?? 0);
    const count = Math.max(0, Math.floor((n - 1 - pos) / step) + 1);
    const data = new Float32Array(count * channels);
    for (let c = 0; c < channels; c++) {
      const x = planes[c];
      const before = prev[c];
      for (let k = 0; k < count; k++) {
        const t = pos + k * step;
        const i = Math.floor(t);
        const f = t - i;
        const a = i < 0 ? before : x[i];
        const b = i + 1 < n ? x[i + 1] : x[n - 1];
        data[c * count + k] = a + (b - a) * f;
      }
      prev[c] = x[n - 1] ?? before;
    }
    const first = pos;
    pos = pos + count * step - n;
    const out = new AudioSample({
      data,
      format: 'f32-planar',
      numberOfChannels: channels,
      sampleRate: outRate,
      timestamp: sample.timestamp + first / sample.sampleRate,
    });
    sample.close();
    return out;
  };
}
